import { useCallback } from 'react';
import { useSortingContext } from 'utils/contexts/sorting';
import { MOVABLE_INK_GET_BLOCKS } from 'api/MovableInkIntegrationsAPI';
import { useRestMutation } from 'utils/hooks/api';
import { MovableInkBlock } from '../types';
import { useInternalPaginationContext } from '../contexts/InternalPagination';

type MovableInkBlocksRequest = {
  tokenId: number;
} & Partial<{
  search: string;
  page: number;
  perPage: number;
  sortBy: string;
  order: string;
  companyId: number;
  campaignId?: number;
}>;

type MovableInkBlocksResponse = {
  blocks: MovableInkBlock[];
  totalPages: number;
};

type MovableInkBlocksRefetchParams = {
  tokenId: number;
} & Partial<{
  campaignId: number;
  search: string;
  companyId: number;
}>;

export const extractLinkFromHref = (htmlString: string): string => {
  const regex = /href="([^"]*)"/;
  const match = htmlString.match(regex);
  return match ? match[1] : '';
};

export const addHrefToBlockMetaData = (blocks: MovableInkBlock[]) =>
  blocks.map((block) => ({
    ...block,
    aHref: extractLinkFromHref(block.embedCode),
  }));

export const useMovableInkBlocksMutation = () => {
  const { sortBy, sortDirection } = useSortingContext();
  const { currentPage, pageSize } = useInternalPaginationContext();

  const {
    data: { blocks = [], totalPages = 0 } = {},
    isLoading,
    isError,
    isSuccess,
    mutate,
  } = useRestMutation<MovableInkBlocksResponse, MovableInkBlocksRequest>(MOVABLE_INK_GET_BLOCKS);

  const refetchBlocks = useCallback(
    ({ tokenId, campaignId, search, companyId }: MovableInkBlocksRefetchParams) => {
      mutate({
        tokenId,
        sortBy,
        search,
        order: sortDirection,
        page: currentPage,
        perPage: pageSize,
        campaignId,
        companyId,
      });
    },
    [sortBy, sortDirection, currentPage, pageSize],
  );

  return {
    blocks: addHrefToBlockMetaData(blocks),
    totalPages,
    isLoading,
    isError,
    isSuccess,
    refetchBlocks,
  };
};
